import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ShoppingBag, Search, Loader2, Star, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";

const CATEGORIES = [
  { value: "", label: "Tout" },
  { value: "textile", label: "Textile" },
  { value: "ustensile", label: "Ustensiles" },
  { value: "epicerie", label: "Épicerie" },
  { value: "livre", label: "Livres" },
];

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const url = category ? `${API}/shop/products?category=${category}` : `${API}/shop/products`;
        const res = await axios.get(url);
        setProducts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category]);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="shop-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="font-heading text-3xl font-bold text-charcoal flex items-center justify-center gap-2">
            <ShoppingBag className="w-7 h-7 text-primary" /> Boutique Gusto
          </h1>
          <p className="text-muted-foreground mt-1">Équipez-vous comme un(e) vrai(e) chef(fe) !</p>
          <p className="text-sm text-primary mt-1 font-medium">10 points = 1€ — Payez avec vos points ou par carte</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="shop-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat.value}
                variant={category === cat.value ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat.value)}
                className="rounded-full"
                data-testid={`filter-${cat.value || "all"}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">Aucun produit trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={`/shop/${product.id}`} data-testid={`product-card-${product.id}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-all group cursor-pointer h-full">
                    <div className="aspect-square bg-muted relative overflow-hidden">
                      <img
                        src={product.featured_image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <Badge className="absolute top-2 left-2 bg-primary text-white text-xs">
                        {product.points_price} pts
                      </Badge>
                    </div>
                    <CardContent className="p-3">
                      <p className="font-medium text-sm text-charcoal truncate">{product.name}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-lg font-bold text-primary">{product.price.toFixed(2)}€</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Truck className="w-3 h-3" /> {product.delivery_days}j
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
